/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import { Search, X, UserCheck } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { authApi } from "../auth/api";
import "./Impersonate.css";

// Modal opened from the AppShell header's "Impersonate user" button
// (visible to IT_ADMIN only, hidden while already impersonating).
//
// Flow:
//   1. Search by name or phone — calls GET /api/auth/impersonate/search.
//   2. Click a row → POST /api/auth/impersonate { target_user_id }.
//   3. Swap token + user blob → hard reload to "/" so the whole app
//      re-renders under the target's identity.
export default function ImpersonatePicker({ open, onClose }) {
  const { setUser } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = useCallback(async (term) => {
    setLoading(true);
    setError("");
    try {
      const tok = localStorage.getItem("access_token");
      const data = await authApi.impersonateSearch(term, tok);
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Initial load — show a few users to choose from with no query.
    search("");
  }, [open, search]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => search(q), 250);
    return () => clearTimeout(id);
  }, [q, open, search]);

  if (!open) return null;

  const become = async (target) => {
    setError("");
    try {
      const tok = localStorage.getItem("access_token");
      const data = await authApi.impersonate(target.id, tok);
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      setUser(data.user);
      window.location.assign("/");
    } catch (err) {
      setError(err?.message || "Could not impersonate");
    }
  };

  return (
    <div className="vsi-overlay" onClick={onClose}>
      <div
        className="vsi-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vsi-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="vsi-modal-head">
          <h2 id="vsi-modal-title">Sign in as another user</h2>
          <button className="vsi-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="vsi-search">
          <Search size={16} />
          <input
            autoFocus
            type="text"
            placeholder="Search by name or phone number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {error && <div className="vsi-modal-error">{error}</div>}

        <div className="vsi-results">
          {loading && results.length === 0 ? (
            <div className="vsi-empty">Searching…</div>
          ) : results.length === 0 ? (
            <div className="vsi-empty">No matching users.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                className="vsi-result"
                onClick={() => become(r)}
              >
                <div className="vsi-result-main">
                  <strong>{r.name || "(unnamed)"}</strong>
                  <span className="vsi-result-phone">{r.phone_number}</span>
                </div>
                <div className="vsi-result-side">
                  {r.villa_label && (
                    <span className="vsi-chip">{r.villa_label}</span>
                  )}
                  {r.admin_role && (
                    <span className="vsi-chip vsi-chip-admin">
                      {r.admin_role}
                    </span>
                  )}
                </div>
                <UserCheck size={16} className="vsi-result-go" />
              </button>
            ))
          )}
        </div>

        <p className="vsi-helper">
          Other IT_ADMINs are hidden — you can't impersonate them.
          Your session as the target lasts up to 1 hour.
        </p>
      </div>
    </div>
  );
}
